import { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import {
    ListBase,
    ListToolbar,
    Pagination,
    Title,
    TopToolbar,
    useListContext,
    useRefresh,
} from 'react-admin';

import {
    type Receipt,
    type ReceiptCategory,
    type ResponsibleUser,
} from './receiptApi';
import { ReceiptAllocationEditDialog } from './ReceiptAllocationEditDialog';
import { ReceiptPreviewDialog } from './ReceiptPreviewDialog';
import { allocationColor, statusColor } from './receiptHelpers';
import { receiptDateRangeFilters } from './ReceiptDateRangeFilters';
import { formatDate } from '@/utils/date_utils';

type Props = {
    categories: ReceiptCategory[];
    users: ResponsibleUser[];
    /** Coworker being helped; null/undefined = receipts the current user uploaded. */
    ownerId?: number | null;
};

export const MyReceiptsList = ({ categories, users, ownerId }: Props) => (
    <ListBase
        resource="receipts"
        storeKey="receipts.mine"
        disableAuthentication
        filter={{ ...(ownerId ? { responsible_user_id: ownerId } : { mine: true }), deleted_at: null }}
        perPage={25}
        sort={{ field: 'id', order: 'DESC' }}
    >
        <MyReceiptsLayout categories={categories} users={users} />
    </ListBase>
);

const MyReceiptsClearFilters = () => {
    const { filterValues, setFilters } = useListContext();
    const hasDateFilter = Boolean(
        filterValues?.transaction_date_gte || filterValues?.transaction_date_lte
    );
    return (
        <TopToolbar>
            <Button
                size="small"
                disabled={!hasDateFilter}
                onClick={() => setFilters({ mine: true }, [])}
            >
                Clear dates
            </Button>
        </TopToolbar>
    );
};

const MyReceiptsLayout = ({ categories, users }: Props) => {
    const { data, isPending, filterValues } = useListContext<Receipt>();
    const stickyKeys = ['mine'];
    const hasFilters = Object.keys(filterValues || {}).some(
        key => !stickyKeys.includes(key)
    );

    return (
        <Stack sx={{ width: '100%', gap: 1 }}>
            <Title title="My receipts" />
            <ListToolbar
                filters={receiptDateRangeFilters}
                actions={<MyReceiptsClearFilters />}
            />
            {!isPending && !data?.length && !hasFilters ? (
                <Alert severity="info">You have not uploaded any receipts yet.</Alert>
            ) : (
                <>
                    <Card>
                        <MyReceiptsContent categories={categories} users={users} />
                    </Card>
                    <Pagination rowsPerPageOptions={[10, 25, 50, 100]} />
                </>
            )}
        </Stack>
    );
};

const MyReceiptsContent = ({ categories, users }: Props) => {
    const { data: receipts = [], isPending, error } = useListContext<Receipt>();
    const refresh = useRefresh();
    const [preview, setPreview] = useState<Receipt | null>(null);
    const [editing, setEditing] = useState<Receipt | null>(null);
    const userName = (id: number | null | undefined) => {
        const user = users.find(item => item.id === id);
        return user?.name || user?.email || 'a coworker';
    };

    if (isPending) return null;
    if (error) {
        return <Alert severity="error">{error.message}</Alert>;
    }

    return (
        <>
            <Stack spacing={0}>
                {receipts.map(receipt => (
                    <CardContent
                        key={receipt.id}
                        sx={{
                            py: 1.5,
                            '&:last-child': { pb: 1.5 },
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            spacing={1}
                        >
                            <Box>
                                <Typography>
                                    {receipt.merchant || receipt.original_filename}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Uploaded{' '}
                                    {formatDate(
                                        receipt.created_at,
                                        'MM-dd-yyyy HH:mm'
                                    )}
                                    {' · '}
                                    {receipt.transaction_date || 'No purchase date'}
                                    {' · '}
                                    {receipt.allocation_type === 'project_cost'
                                        ? 'Project cost (COGS)'
                                        : receipt.category}
                                    {receipt.subcategory
                                        ? `: ${receipt.subcategory}`
                                        : ''}
                                    {receipt.project_no != null
                                        ? ` · Project #${receipt.project_no}`
                                        : ''}
                                </Typography>
                                {receipt.created_by != null &&
                                    receipt.created_by !== receipt.responsible_user_id && (
                                        <Typography variant="body2" color="info.main">
                                            Uploaded by {userName(receipt.created_by)} for{' '}
                                            {userName(receipt.responsible_user_id)}
                                        </Typography>
                                    )}
                                {receipt.allocation_status === 'needs_correction' && (
                                    <Typography
                                        variant="body2"
                                        color="error.main"
                                        mt={0.5}
                                    >
                                        Bookkeeper correction requested:{' '}
                                        {receipt.allocation_review_notes}
                                    </Typography>
                                )}
                            </Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {receipt.amount && (
                                    <Typography>${receipt.amount}</Typography>
                                )}
                                <Button size="small" onClick={() => setEditing(receipt)}>
                                    Edit allocation
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => setPreview(receipt)}
                                >
                                    View
                                </Button>
                                <Chip
                                    size="small"
                                    label={`allocation ${receipt.allocation_status.replaceAll('_', ' ')}`}
                                    color={allocationColor(receipt.allocation_status)}
                                />
                                <Chip
                                    size="small"
                                    label={receipt.status}
                                    color={statusColor(receipt.status)}
                                />
                            </Stack>
                        </Stack>
                    </CardContent>
                ))}
            </Stack>
            <ReceiptPreviewDialog
                receipt={preview}
                onClose={() => setPreview(null)}
            />
            <ReceiptAllocationEditDialog
                receipt={editing}
                categories={categories}
                users={users}
                onClose={() => setEditing(null)}
                onSaved={() => refresh()}
            />
        </>
    );
};
